
import { useState } from "react";
import { Table, TableBody, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import UserTableRow from "./UserTableRow";
import MobileUserCard from "./MobileUserCard";
import type { Database } from "@/integrations/supabase/types";

type UserProfile = Database['public']['Tables']['profiles']['Row'];

interface UsersTableProps {
  users: UserProfile[] | undefined;
  loading: boolean;
  onEditUser: (user: UserProfile) => void;
  onVerifyUser: (user: UserProfile) => void;
  onDeleteUser?: (user: UserProfile) => void;
  onManageCountries?: (user: UserProfile) => void;
  isMobile: boolean;
  pagination: {
    currentPage: number;
    setCurrentPage: (page: number) => void;
    itemsPerPage: number;
    setItemsPerPage: (items: number) => void;
  };
  totalUsers: number;
}

const UsersTable = ({ 
  users = [], 
  loading, 
  onEditUser, 
  onVerifyUser, 
  onDeleteUser, 
  onManageCountries,
  isMobile,
  pagination,
  totalUsers 
}: UsersTableProps) => {
  const { currentPage, setCurrentPage, itemsPerPage } = pagination;
  const totalPages = Math.ceil(totalUsers / itemsPerPage);
  
  // Ensure users is always an array
  const safeUsers = users || [];

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-nation-blue"></div>
      </div>
    );
  }

  if (isMobile) {
    return (
      <div className="space-y-4">
        <div className="grid gap-4">
          {safeUsers.map((user) => (
            <MobileUserCard
              key={user.id}
              user={user}
              onEdit={onEditUser}
              onVerify={onVerifyUser}
              onDelete={onDeleteUser}
              onManageCountries={onManageCountries}
            />
          ))}
        </div>
        
        {/* Mobile Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-between items-center pt-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>
            
            <span className="text-sm text-gray-600">
              Page {currentPage} of {totalPages}
            </span>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Country</TableHead>
              <TableHead>Constituency</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {safeUsers.map((user) => (
              <UserTableRow
                key={user.id}
                user={user}
                onEdit={onEditUser}
                onVerify={onVerifyUser}
                onDelete={onDeleteUser}
                onManageCountries={onManageCountries}
              />
            ))}
          </TableBody>
        </Table>
      </div>
      
      {/* Desktop Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600">
              Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, totalUsers)} of {totalUsers} users
            </span>
          </div>
          
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>
            
            <span className="text-sm text-gray-600">
              Page {currentPage} of {totalPages}
            </span>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersTable;
